/**
 * LeadTransitionForm.tsx — the operator state-transition write UI (LS8, Q1=YES).
 *
 * Presents ONLY the `allowed_transitions` the detail DTO returns for the lead's
 * current status — illegal edges are never offered, so the happy path can never
 * attempt one (the source of truth is the server-computed matrix, not a list
 * re-derived here). The write goes to `POST /api/leads/{id}/transition` with a
 * `LeadTransitionRequest` ({to_status, note?}); `actor` is set server-side and
 * is NOT part of the contract — it is never sent.
 *
 * Because the operator is mutating audited state, submission is confirm-style: a
 * "review" step reveals a confirmation panel (focus moves to it — WCAG 2.4.3 /
 * 3.2.2) summarising from→to before the write fires; the control is disabled and
 * `aria-busy` while in flight (covers the double-click race). A quarantine
 * release requires a reason note before submit is enabled.
 *
 * CRITICAL — distinct write-error UX (the BFF propagates these so the UI need
 * not lie about which happened):
 *   422 → illegal/invalid transition: show the BFF's actionable `detail`, NOT
 *         "service down" — keep the form open so the operator can adjust.
 *   409 → lost race / concurrent change: tell them to reload (the lead moved);
 *         the reload button refetches the detail.
 *   404 → the lead is gone / no longer visible.
 *   502 → service unavailable: a clean "try again".
 * These are never collapsed into one generic error.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import type { LeadStatus } from '../../api/types';
import floating from '../../lib/floating';
import { Button } from '../../components/Button';
import { Field, inputClass } from '../../components/Field';

export interface LeadTransitionFormProps {
  apiClient: ApiClient;
  leadId: string;
  currentStatus: LeadStatus;
  allowedTransitions: LeadStatus[];
  /** True when the lead is in `quarantine` — release requires a reason note. */
  requiresNote: boolean;
  /** Called after a successful transition (and on a 409 reload) to refetch. */
  onRefetch: () => Promise<void>;
}

type WriteError =
  | { kind: 'illegal'; message: string }
  | { kind: 'conflict' }
  | { kind: 'notFound' }
  | { kind: 'unavailable' }
  | { kind: 'unauthorized' };
/** Distinct, status-specific write-error rendering (never one generic message). */
const TransitionError = ({
  error,
  onReload,
}: {
  error: WriteError;
  onReload: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const baseClass =
    'rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40';

  if (error.kind === 'illegal') {
    return (
      <p role="alert" data-testid="transition-error-illegal" className={baseClass}>
        {t('leadDetail.transition.errors.illegal')}: {error.message}
      </p>
    );
  }
  if (error.kind === 'conflict') {
    return (
      <div role="alert" data-testid="transition-error-conflict" className="flex flex-col gap-2">
        <p className={baseClass}>{t('leadDetail.transition.errors.conflict')}</p>
        <Button
          data-testid="transition-conflict-reload"
          onClick={() => floating(onReload())}
          className="min-h-11"
        >
          {t('leadDetail.transition.errors.reload')}
        </Button>
      </div>
    );
  }
  if (error.kind === 'notFound') {
    return (
      <p role="alert" data-testid="transition-error-notfound" className={baseClass}>
        {t('leadDetail.transition.errors.notFound')}
      </p>
    );
  }
  if (error.kind === 'unauthorized') {
    return (
      <p role="alert" data-testid="transition-error-unauthorized" className={baseClass}>
        {t('leadDetail.transition.errors.unauthorized')}
      </p>
    );
  }
  return (
    <p role="alert" data-testid="transition-error-unavailable" className={baseClass}>
      {t('leadDetail.transition.errors.unavailable')}
    </p>
  );
};

export const LeadTransitionForm = ({
  apiClient,
  leadId,
  currentStatus,
  allowedTransitions,
  requiresNote,
  onRefetch,
}: LeadTransitionFormProps) => {
  const { t } = useTranslation();
  const [toStatus, setToStatus] = useState<LeadStatus | ''>('');
  const [note, setNote] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [writeError, setWriteError] = useState<WriteError | null>(null);

  const confirmRef = useRef<HTMLDivElement>(null);

  // Move focus to the confirmation panel when it opens (WCAG 2.4.3).
  useEffect(() => {
    if (reviewing) confirmRef.current?.focus();
  }, [reviewing]);

  if (allowedTransitions.length === 0) {
    return (
      <p data-testid="lead-transition-none" className="text-body text-ink-2">
        {t('leadDetail.transition.none')}
      </p>
    );
  }

  const noteMissing = requiresNote && note.trim() === '';
  const canReview = toStatus !== '' && !noteMissing && !submitting;

  const handleConfirm = async () => {
    if (toStatus === '') return;
    setSubmitting(true);
    setWriteError(null);
    try {
      // `actor` is intentionally absent — the contract has no actor field; the
      // service stamps it. `note` is omitted when blank (optional in contract).
      await apiClient.transitionLead(leadId, {
        to_status: toStatus,
        ...(note.trim() !== '' ? { note: note.trim() } : {}),
      });
      // Success: clear the form and refetch so the new status + the new history
      // entry appear.
      setReviewing(false);
      setToStatus('');
      setNote('');
      await onRefetch();
    } catch (error) {
      setReviewing(false);
      if (error instanceof ApiError && error.status === 422) {
        // Illegal / invalid edge — surface the BFF's actionable detail.
        setWriteError({
          kind: 'illegal',
          message: error.detail ?? t('leadDetail.transition.errors.illegalFallback'),
        });
      } else if (error instanceof ApiError && error.status === 409) {
        setWriteError({ kind: 'conflict' });
      } else if (error instanceof ApiError && error.status === 404) {
        setWriteError({ kind: 'notFound' });
      } else if (error instanceof UnauthorizedError) {
        setWriteError({ kind: 'unauthorized' });
      } else {
        // ApiError 502 / transport / 5xx — service unavailable.
        setWriteError({ kind: 'unavailable' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      aria-labelledby="lead-transition-heading"
      data-testid="lead-transition-form"
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (canReview) setReviewing(true);
      }}
    >
      <h3 id="lead-transition-heading" className="text-h3 text-ink-1">
        {t('leadDetail.transition.heading')}
      </h3>

      <Field id="transition-to-status" label={t('leadDetail.transition.toStatus')}>
        <select
          id="transition-to-status"
          data-testid="transition-to-status"
          value={toStatus}
          disabled={submitting}
          onChange={(event) => {
            setToStatus(event.target.value as LeadStatus);
            setReviewing(false);
            setWriteError(null);
          }}
          className={inputClass}
        >
          <option value="">{t('leadDetail.transition.choose')}</option>
          {allowedTransitions.map((status) => (
            <option key={status} value={status}>
              {t(`leadStatus.${status}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id="transition-note"
        label={
          requiresNote
            ? t('leadDetail.transition.noteRequired')
            : t('leadDetail.transition.noteOptional')
        }
        hint={requiresNote ? t('leadDetail.transition.noteHint') : undefined}
      >
        <textarea
          id="transition-note"
          data-testid="transition-note"
          value={note}
          disabled={submitting}
          rows={2}
          aria-required={requiresNote}
          onChange={(event) => {
            setNote(event.target.value);
            setReviewing(false);
          }}
          className={inputClass}
        />
      </Field>

      {!reviewing ? (
        <Button
          type="submit"
          variant="primary"
          data-testid="transition-review"
          disabled={!canReview}
          className="min-h-11"
        >
          {t('leadDetail.transition.review')}
        </Button>
      ) : null}

      {reviewing && toStatus !== '' ? (
        <div
          ref={confirmRef}
          tabIndex={-1}
          role="group"
          aria-label={t('leadDetail.transition.confirmHeading')}
          data-testid="transition-confirm"
          className="flex flex-col gap-3 rounded-lg bg-surface-2 p-4 ring-1 ring-inset ring-gold/40 focus:outline-none focus:ring-2 focus:ring-outline-focus"
        >
          <p className="text-body text-ink-1">
            {t('leadDetail.transition.confirmBody', {
              from: t(`leadStatus.${currentStatus}`),
              to: t(`leadStatus.${toStatus}`),
            })}
          </p>
          <div className="flex gap-3">
            <Button
              variant="primary"
              data-testid="transition-confirm-submit"
              disabled={submitting}
              aria-busy={submitting}
              onClick={() => floating(handleConfirm())}
              className="min-h-11"
            >
              {submitting
                ? t('leadDetail.transition.submitting')
                : t('leadDetail.transition.confirm')}
            </Button>
            <Button
              data-testid="transition-cancel"
              disabled={submitting}
              onClick={() => setReviewing(false)}
              className="min-h-11"
            >
              {t('leadDetail.transition.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {writeError != null ? <TransitionError error={writeError} onReload={onRefetch} /> : null}
    </form>
  );
};
